
import { Link } from 'react-router-dom';

export default function FourElementsPage() {
  const elements = [
    {
      number: '①',
      title: '役割の明確化（Role Clarification）',
      badge: '最重要',
      importance: '最も強くパフォーマンスに影響',
      description: '誰が何をどこまで担うか、最終判断者・責任者の明確化、相互依存関係の整理、属人化の排除',
      icon: 'ri-user-settings-line',
      color: 'from-rose-600 to-pink-600',
      practices: [
        {
          title: '役割マトリクスの作成',
          description: '業務ごとに「実行者」「承認者」「相談先」「情報共有先」を明確化'
        },
        {
          title: '判断基準の共有',
          description: '「この場合は誰が判断するか」を事前に決めておく'
        },
        {
          title: '属人業務の可視化',
          description: '特定の人しかできない業務をリストアップし、手順化・共有化'
        },
        {
          title: '相互依存関係の整理',
          description: '誰の仕事が誰に影響するかを図式化し、連携ポイントを明確化'
        }
      ],
      checkpoints: [
        '各メンバーが自分の役割を説明できるか',
        '判断に迷ったとき、誰に相談すべきか明確か',
        '休暇や欠勤時に業務が止まらないか',
        '役割の重複や漏れがないか'
      ]
    },
    {
      number: '②',
      title: '目標設定（Goal Setting）',
      badge: '方向性',
      importance: '努力の方向性を揃える要素',
      description: 'チームとしての成功定義、数値目標と行動目標の分離、優先順位の共有、忙しい時の判断基準の一致',
      icon: 'ri-flag-line',
      color: 'from-pink-600 to-rose-500',
      practices: [
        {
          title: '成功の定義を共有',
          description: '「何ができたら成功か」をチーム全員で言語化する'
        },
        {
          title: '数値目標と行動目標の分離',
          description: '結果（処方箋枚数）と行動（在宅訪問回数）を分けて設定'
        },
        {
          title: '優先順位の明確化',
          description: '複数の目標がある場合、優先順位を明示する'
        },
        {
          title: '判断基準の共有',
          description: '忙しい時に「何を優先するか」の基準を事前に決めておく'
        }
      ],
      checkpoints: [
        'チームの目標を全員が説明できるか',
        '数値目標だけでなく行動目標も設定されているか',
        '忙しい時の優先順位が明確か',
        '目標達成のための具体的な行動が共有されているか'
      ]
    },
    {
      number: '③',
      title: '対人関係（Interpersonal Relations）',
      badge: '心理的側面',
      importance: '満足度・心理的安全性には強く効くが、単独では成果に直結しない',
      description: '信頼関係、コミュニケーションの質、感情的サポート、帰属意識',
      icon: 'ri-heart-line',
      color: 'from-rose-500 to-pink-500',
      practices: [
        {
          title: '定期的な1on1',
          description: '評価面談ではなく、学習・成長のための対話の場として設計'
        },
        {
          title: '感謝の言語化',
          description: '「ありがとう」を具体的に伝える習慣をつくる'
        },
        {
          title: '雑談の時間確保',
          description: '業務外の会話が自然に生まれる場をつくる'
        },
        {
          title: 'チームイベント',
          description: '定期的な食事会や勉強会で帰属意識を高める'
        }
      ],
      checkpoints: [
        'メンバー同士が気軽に相談できる関係か',
        '困ったときに助けを求められる雰囲気か',
        'チームの一員であることに誇りを持てているか',
        '感謝や承認が日常的に交わされているか'
      ]
    },
    {
      number: '④',
      title: '問題解決（Problem Solving）',
      badge: '改善',
      importance: '役割・目標・安全性が整って初めて効果を発揮',
      description: '課題の可視化、原因分析、改善策の立案と実行、プロセス改善',
      icon: 'ri-lightbulb-line',
      color: 'from-pink-500 to-rose-400',
      practices: [
        {
          title: '定期的な振り返り',
          description: '週次・月次で「うまくいったこと」「改善すべきこと」を共有'
        },
        {
          title: 'ヒヤリハットの構造化',
          description: '個人の責任追及ではなく、システム改善の機会として扱う'
        },
        {
          title: '改善提案の仕組み化',
          description: '誰でも気軽に改善提案できる仕組みをつくる'
        },
        {
          title: 'PDCAの習慣化',
          description: '小さな改善を継続的に回す文化をつくる'
        }
      ],
      checkpoints: [
        '問題が早期に共有される仕組みがあるか',
        '原因分析が個人攻撃にならないか',
        '改善策が実行され、効果が検証されているか',
        '失敗から学ぶ文化があるか'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-rose-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-rose-600 transition-colors duration-200">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/team-building" className="hover:text-rose-600 transition-colors duration-200">チームビルディング</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-rose-600 font-medium">四大要素</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl mb-6 shadow-lg">
              <i className="ri-stack-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Ⅱ．チームビルディングの四大要素
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              科学的コンセンサスに基づく4つの重要要素
            </p>
          </div>
        </div>
      </section>

      {/* Elements */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-12">
            {elements.map((element, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-rose-100 overflow-hidden">
                <div className={`bg-gradient-to-r ${element.color} p-6`}>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl">
                      <i className={`${element.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl font-bold text-white">{element.number}</span>
                        <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-white">
                          {element.badge}
                        </span>
                      </div>
                      <h2 className="text-2xl font-bold text-white">{element.title}</h2>
                    </div>
                  </div>
                  <p className="text-white/90 text-sm mt-4">{element.description}</p>
                </div>

                <div className="p-8">
                  <div className="bg-rose-50 rounded-xl p-6 mb-8">
                    <h3 className="text-lg font-bold text-rose-900 mb-2 flex items-center gap-2">
                      <i className="ri-information-line"></i>
                      重要性
                    </h3>
                    <p className="text-gray-700">{element.importance}</p>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-6">実践方法</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                    {element.practices.map((practice, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-checkbox-circle-line text-rose-600"></i>
                          {practice.title}
                        </h4>
                        <p className="text-sm text-gray-600">{practice.description}</p>
                      </div>
                    ))}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-4">チェックポイント</h3>
                  <ul className="space-y-3">
                    {element.checkpoints.map((checkpoint, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-700">
                        <i className="ri-arrow-right-s-line text-rose-600 mt-1 flex-shrink-0"></i>
                        <span>{checkpoint}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-start gap-6">
                <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-4">明日の一手</h2>
                  <p className="text-lg leading-relaxed mb-6">
                    まずは「①役割の明確化」から始めましょう。次回のミーティングで、各メンバーの役割と責任範囲を確認し、不明確な部分や重複している部分を洗い出してください。役割が明確になるだけで、チームのパフォーマンスは大きく向上します。
                  </p>
                  <div className="flex items-center gap-2 text-sm bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg inline-flex whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    <span>所要時間：30〜45分</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link
                to="/team-building/core-concepts"
                className="flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i>
                <span>前へ：中核概念</span>
              </Link>
              <Link
                to="/team-building/psychological-safety"
                className="flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                <span>次へ：心理的安全性</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
